/**
 * Theme system for Turn Connect applications.
 * Comprehensive design system with colors, spacing, typography, and more.
 */

import { type Colors, colors as darkPalette } from './colors';
import { lightColors as lightPalette } from './colors.light';
import { colors } from './resolvedColors';
import { injectThemeVars, generateThemeCSS } from './cssVars';
import { colorTokens, tokenVars, flattenTokens, tokenCssVar, type ColorTokens, type TokenScheme, type TokenPath } from './tokens';
import { withAlpha } from './colorUtils';
import {
  textRoles,
  nativeTextRole,
  statTileSpans,
  sectionSpec,
  compactSpec,
  EMPTY_VALUE,
  type Density,
  type TextRole,
  type TextRoleName,
  type TextRoleColor,
} from './textRoles';
import { textRole, compactVars } from './textRoleStyles';
import { ACCENTS, resolveAccent, accentSizes, type AccentKey, type AccentIconName, type AccentSize, type ResolvedAccent } from './accents';
import {
  logoMarkSizes,
  logoInitials,
  logoMarkPlate,
  type LogoMarkSize,
  type LogoMarkFrame,
  type LogoMarkState,
  type LogoMarkPlate,
} from './logoMark';
import { sectionColorSpec, alphaHex, mixHex } from './sectionColor';
import { spacing, type Spacing } from './spacing';
import { typography, type Typography } from './typography';
import { animations, type Animations } from './animations';
import { breakpoints, type Breakpoints } from './breakpoints';
import { shadows, elevation, borders, focusRing, type Shadows, type Elevation, type Borders, type FocusRing } from './effects';
import { SIZE_CONFIGS, getSizeConfig, ENTITY_MODAL, type ComponentSize, type SizeConfig } from './sizes';
import { scrollbars, injectScrollbarStyles, type ScrollbarStyles } from './scrollbars';
import { tabStyles, tabClasses, injectTabStyles, chosenTabLook } from './tabs';
import { zIndex, type ZIndex } from './zIndex';
import {
  approvalStatusStyles,
  approvalSurfaceStyle,
  normalizeApprovalStatus,
  type ApprovalStatusKey,
  type ApprovalStatusStyle,
} from './approval';

export const theme = {
  colors,
  spacing,
  typography,
  animations,
  breakpoints,
  shadows,
  elevation,
  borders,
  focusRing,
  scrollbars,
  tabStyles,
  tabClasses,
  sizes: { SIZE_CONFIGS, getSizeConfig, ENTITY_MODAL },
  zIndex,
  approvalStatus: approvalStatusStyles,
};

export type Theme = typeof theme;

// Re-export for convenience
export {
  colors,
  injectThemeVars,
  generateThemeCSS,
  withAlpha,
  darkPalette,
  lightPalette,
  spacing,
  typography,
  animations,
  breakpoints,
  shadows,
  elevation,
  borders,
  focusRing,
  scrollbars,
  injectScrollbarStyles,
  tabStyles,
  tabClasses,
  injectTabStyles,
  chosenTabLook,
  SIZE_CONFIGS,
  getSizeConfig,
  ENTITY_MODAL,
  zIndex,
  approvalStatusStyles,
  approvalSurfaceStyle,
  normalizeApprovalStatus,
  colorTokens,
  tokenVars,
  flattenTokens,
  tokenCssVar,
  textRoles,
  textRole,
  nativeTextRole,
  statTileSpans,
  sectionSpec,
  compactSpec,
  compactVars,
  EMPTY_VALUE,
  ACCENTS,
  resolveAccent,
  accentSizes,
  logoMarkSizes,
  logoInitials,
  sectionColorSpec,
  alphaHex,
  mixHex,
  logoMarkPlate,
};

export type {
  LogoMarkSize,
  LogoMarkFrame,
  LogoMarkState,
  LogoMarkPlate,
  Colors,
  Spacing,
  Typography,
  Animations,
  Breakpoints,
  Shadows,
  Elevation,
  Borders,
  FocusRing,
  ScrollbarStyles,
  ComponentSize,
  SizeConfig,
  ZIndex,
  ApprovalStatusKey,
  ApprovalStatusStyle,
  ColorTokens,
  TokenScheme,
  TokenPath,
  TextRole,
  TextRoleName,
  TextRoleColor,
  Density,
  AccentKey,
  AccentIconName,
  AccentSize,
  ResolvedAccent,
};

/**
 * Hook to access the theme in React components.
 * For future integration with theme providers.
 */
export const useTheme = () => theme;

export { TYPE_SCALE, TYPE_FLOOR, scaleFontSize, scaleLineHeight } from './typeScale';
